



import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Offcanvas, Button, Modal, Form, Spinner, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import {
    FaBuilding,
    FaTag,
    FaCalendar,
    FaDollarSign,
    FaFileInvoice,
    FaUsers,
    FaExchangeAlt,
    FaHistory,
    FaUsersCog,
    FaShoppingCart,
    FaFileContract,
    FaListAlt
} from 'react-icons/fa';
import { BiPencil } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import ProjectView from './ProjectView';
import ProjectList from './ProjectList';
import CommunicationDetails from './CommunicationDetails';
import ProjectTeam from './ProjectTeam';
import ProjectSignoff from '../projects/ProjectSignoff';
import ProjectWorkflow from '../projects/ProjectWorkflow';
import QuotationList from '../quotation/QuotationList';
import SelectedContact from '../contact/SelectedContact';
import ProformaInvoiceList from '../ProformaInvoice/ProformaInvoiceList';
import withPermission from '../../utils/withPermission';
import ExpenseList from './expense/ExpenseList';
import ProjectModal from './ProjectModal';

// Fixed: Moved permission wrappers outside to prevent remounting on every render
const WithSubPermission = withPermission(({ children }) => children, ['project-list']);
const WithCommuniPermission = withPermission(({ children }) => children, ['communication-list']);
const WithQuoPermission = withPermission(({ children }) => children, ['quotation-list']);
const WithContactsPermission = withPermission(({ children }) => children, ['contact-list']);
const WithExpensePermission = withPermission(({ children }) => children, ['expense-list']);

const ProjectDetails = ({ show, onHide, projectId, parentId }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get("activeTab") || "subprojectList";

    const [projectDetails, setProjectDetails] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [quotationHistory, setQuotationHistory] = useState([]);
    const [isSales, setIsSales] = useState(false);
    const [isSubproject, setIsSubproject] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [clients, setClients] = useState([]);
    const [projectOrders, setProjectOrders] = useState([]);
    const [projectVendorPOs, setProjectVendorPOs] = useState([]);
    const [isMinimizing, setIsMinimizing] = useState(false);
    const isMinimizingRef = useRef(false);
    const [localShow, setLocalShow] = useState(show);
    const [forceShowContactModal, setForceShowContactModal] = useState(false);

    // Order Modal state
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [orderFormData, setOrderFormData] = useState({
        id: null,
        name: '',
        po_number: '',
        po_attachment: null,
        po_value: '',
        recurring_cost: '',
        one_time_cost: '',
        issue_date: '',
        isRenewal: false
    });

    const handleShowOrderModal = (existingOrder = null) => {
        if (existingOrder) {
            setOrderFormData({
                id: existingOrder.id,
                name: existingOrder.name.includes('Renewal') ? existingOrder.name : `${existingOrder.name} - Renewal`,
                po_number: existingOrder.po_number || existingOrder.external_id || '',
                po_attachment: null,
                po_value: existingOrder.po_value || existingOrder.total_value || existingOrder.value || '',
                recurring_cost: existingOrder.recurring_cost || '',
                one_time_cost: existingOrder.one_time_cost || '',
                issue_date: dayjs().format('YYYY-MM-DD'),
                isRenewal: true
            });
        } else {
            setOrderFormData({
                id: null,
                name: projectDetails?.name ? `${projectDetails.name} - Order` : '',
                po_number: '',
                po_attachment: null,
                po_value: '',
                recurring_cost: '',
                one_time_cost: '',
                issue_date: dayjs().format('YYYY-MM-DD'),
                isRenewal: false
            });
        }
        setIsMinimizing(true);
        setShowOrderModal(true);
    };

    const handleCloseOrderModal = () => {
        setShowOrderModal(false);
        setIsMinimizing(false);
    };

    const handleDeleteOrder = async (order) => {
        try {
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            });

            if (result.isConfirmed) {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const token = userInfo?.token;
                const origin = localStorage.getItem('user-origin');

                // Determine which endpoint to use based on source
                const endpoint = order.source === 'deal' 
                    ? `${backendUrl}/api/deals/${order.id}`
                    : `${backendUrl}/api/project-orders/${order.id}`;

                await axios.delete(endpoint, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin }
                });

                Swal.fire('Deleted!', 'The order has been deleted.', 'success');
                fetchProjectOrders();
            }
        } catch (error) {
            console.error('Error deleting order:', error);
            Swal.fire('Error!', 'Failed to delete the order. Check your permissions.', 'error');
        }
    };

    const handleOrderSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingOrder(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const formData = new FormData();
            formData.append('project_id', projectId || id);
            formData.append('organisation_id', projectDetails?.client_id);
            formData.append('client_id', projectDetails?.client_id);
            formData.append('name', orderFormData.name);
            formData.append('po_number', orderFormData.po_number);
            formData.append('total_value', orderFormData.po_value);
            formData.append('recurring_cost', orderFormData.recurring_cost);
            formData.append('one_time_cost', orderFormData.one_time_cost);
            formData.append('issue_date', orderFormData.issue_date);
            formData.append('is_renewal', orderFormData.isRenewal ? 1 : 0);
            formData.append('is_direct', 1);

            if (orderFormData.po_attachment) {
                formData.append('po_attachment', orderFormData.po_attachment);
            }

            await axios.post(`${backendUrl}/api/project-orders`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Content-Type': 'multipart/form-data'
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Order Created',
                text: orderFormData.isRenewal ? 'The renewal order has been created.' : 'The new order has been created.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });

            handleCloseOrderModal();
            fetchProjectOrders();
        } catch (error) {
            console.error('Error creating order:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create order.'
            });
        } finally {
            setIsSubmittingOrder(false);
        }
    };

    // Preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewTitle, setPreviewTitle] = useState("");

    const handleFilePreview = (fileUrl, title) => {
        setPreviewFile(fileUrl);
        setPreviewTitle(title);
        setShowPreview(true);
    };

    // Sync ref with state
    useEffect(() => {
        isMinimizingRef.current = isMinimizing;
    }, [isMinimizing]);

    // Sync with parent show prop
    useEffect(() => {
        setLocalShow(show);
    }, [show]);

    // Fetch project details
    const fetchProjectDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/projects/${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            console.log('PROJECT FETCHED:', {
                id: currentId,
                status: response.status,
                data: response.data.Data
            });

            const data = response.data.Data;
            setProjectDetails(data);
            setIsSubproject(!!data.parent_id);
            setIsSales(!!data.issales);
        } catch (error) {
            console.error('Error fetching project details:', error);
            if (error.response) {
                console.log('?? ERROR RESPONSE:', error.response.data);
            }
        }
    };

    // Fetch quotation history
    const fetchQuotationHistory = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/quotations/${currentId}/quotation-history`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setQuotationHistory(response.data.data || []);
        } catch (error) {
            console.error('Error fetching quotation history:', error);
        }
    };

    // Fetch clients
    const fetchClients = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setClients(response.data.Data || []);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    // Fetch project orders
    const fetchProjectOrders = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            
            // Fetch from both sources in parallel
            const [dealsRes, projectOrdersRes] = await Promise.all([
                axios.get(`${backendUrl}/api/deals?project_id=${currentId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                    skipLoader: true
                }),
                axios.get(`${backendUrl}/api/project-orders?project_id=${currentId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                    skipLoader: true
                })
            ]);

            // Process Deals
            const rawDeals = dealsRes.data.deals?.data || dealsRes.data.deals || dealsRes.data.data || (Array.isArray(dealsRes.data) ? dealsRes.data : []);
            const validDeals = rawDeals.filter(d => d.stage === 'order').map(d => ({...d, source: 'deal'}));

            // Process Project Orders
            const rawProjectOrders = projectOrdersRes.data.data || (Array.isArray(projectOrdersRes.data) ? projectOrdersRes.data : []);
            const validProjectOrders = rawProjectOrders.map(o => ({...o, source: 'project_order'}));

            // Combine and sort, ensuring unique PO numbers (prefer project_order over deal)
            const combined = [...validProjectOrders];
            
            // Only add deals that don't have a corresponding project order (match by po_number)
            validDeals.forEach(deal => {
                const isDuplicate = validProjectOrders.some(po => 
                    po.po_number && deal.po_number && po.po_number.trim() === deal.po_number.trim()
                );
                if (!isDuplicate) {
                    combined.push(deal);
                }
            });

            combined.sort((a, b) => {
                const dateA = a.issue_date || a.closed_date ? new Date(a.issue_date || a.closed_date) : new Date(0);
                const dateB = b.issue_date || b.closed_date ? new Date(b.issue_date || b.closed_date) : new Date(0);
                return dateB - dateA; // Descending
            });

            setProjectOrders(combined);
        } catch (error) {
            console.error('Error fetching project orders:', error);
        }
    };

    // Fetch project vendor POs
    const fetchProjectVendorPOs = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/purchase-order?project_id=${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            // Handle various response structures (paginated, direct key, or direct data)
            const posData = 
                response.data.purchaseorders?.data || 
                response.data.purchaseorders || 
                response.data.data || 
                (Array.isArray(response.data) ? response.data : []);

            setProjectVendorPOs(posData);
        } catch (error) {
            console.error('Error fetching project vendor POs:', error);
        }
    };

    // Edit project function
    const editProject = async (projectId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            console.log('SENDING PROJECT:', {
                id: projectId,
                payload: updatedData instanceof FormData ? Object.fromEntries(updatedData.entries()) : updatedData
            });

            const response = await axios.post(
                `${backendUrl}/api/projects/${projectId}?_method=PUT`,
                updatedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                        'Tenant-Origin': origin,
                    },
                }
            );

            console.log('UPDATE RESPONSE:', {
                status: response.status,
                updatedData: response.data.Data
            });

            const updatedProject = response.data.Data;

            if (updatedProject) {
                setProjectDetails((prevProject) => ({
                    ...prevProject,
                    ...updatedProject,
                }));
            }

            setShowModal(false);
        } catch (error) {
            console.log('?? ERROR UPDATING PROJECT ON SERVER:', error.response?.data || error.message);
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            } else {
                console.error('Error updating project:', error);
            }
        }
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Format action helper
    const formatAction = (action) => {
        if (!action) return '';
        return action
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        if (localShow && (projectId || id)) {
            console.log('INITIALIZING PROJECT DETAILS:', { projectId, id, localShow });
            setProjectDetails(null);
            fetchProjectDetails();
            fetchClients();
        }
    }, [localShow, projectId, id]);

    useEffect(() => {
        if (localShow && activeTab === 'quotationHistory') {
            fetchQuotationHistory();
        }
        if (localShow && activeTab === 'orders') {
            fetchProjectOrders();
        }
        if (localShow && activeTab === 'vendorPOs') {
            fetchProjectVendorPOs();
        }
    }, [localShow, activeTab]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleOffcanvasClose = () => {
        setProjectDetails(null);
        setActiveTab('subprojectList');
        onHide();
    };

    // Callback functions for temporarily hiding the offcanvas
    const temporarilyHideOffcanvas = () => {
        setIsMinimizing(true);
    };

    const reopenOffcanvas = () => {
        setIsMinimizing(false);
    };

    // const shouldShowOffcanvas = show && !isMinimizing;

    return (
        <>
            <Offcanvas
                show={localShow && !isMinimizing}
                onHide={() => {
                    if (!isMinimizingRef.current) handleOffcanvasClose();
                }}
            placement="end"
            style={{ 
                width: '1250px', 
                zIndex: 1045,
                ...(isMinimizing ? { visibility: 'hidden', opacity: 0, pointerEvents: 'none' } : {})
            }}
            backdrop={!isMinimizing}
            backdropClassName={isMinimizing ? 'd-none' : ''}
            className={`Project-offcanvas ${isMinimizing ? 'minimized' : ''}`}
            enforceFocus={false}
            autoFocus={false}
            restoreFocus={false}
            unmountOnExit={false}
        >
            {/* Offcanvas Header */}
            <Offcanvas.Header className="border-bottom bg-white py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle p-2 me-3">
                            <FaBuilding className="text-white" size={18} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">{projectDetails?.name}</h5>
                            <small className="text-muted">ID: {projectDetails?.code || projectId || id}</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                setSelectedProject(projectDetails);
                                setShowModal(true);
                            }}
                        >
                            <BiPencil className="me-1" />
                            Edit
                        </Button>
                        <Button
                            variant="light"
                            size="sm"
                            onClick={handleOffcanvasClose}
                            className="border-0"
                        >
                            <BsX size={20} />
                        </Button>
                    </div>
                </div>
            </Offcanvas.Header>

            {/* Offcanvas Body */}
            <Offcanvas.Body className="p-0">
                {/* Project Summary Card */}
                <div className="p-4 border-bottom bg-light">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaTag className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Project Code</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.code || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaCalendar className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Start Date</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {formatDate(projectDetails?.start_date) || 'Not specified'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaDollarSign className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Expenditure Amount</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.expenditure_amount || '0.00'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaUsers className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Client</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.client?.name || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="border-bottom bg-white">
                    <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                        {!isSales && !isSubproject && (
                            <WithSubPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'subprojectList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('subprojectList')}
                                        role="tab"
                                    >
                                        <FaBuilding className="me-2" size={14} />
                                        Sub Projects
                                    </button>
                                </li>
                            </WithSubPermission>
                        )}

                        <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationList' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationList')}
                                    role="tab"
                                >
                                    <FaFileInvoice className="me-2" size={14} />
                                    Quotations
                                </button>
                            </li>
                        </WithQuoPermission>
                          <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
                                onClick={() => handleTabClick('orders')}
                                role="tab"
                            >
                                <FaShoppingCart className="me-2" size={14} />
                                Orders
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'vendorPOs' ? 'active' : ''}`}
                                onClick={() => handleTabClick('vendorPOs')}
                                role="tab"
                            >
                                <FaFileContract className="me-2" size={14} />
                                Vendor POs
                            </button>
                        </li>

                        {/* <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationHistory' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationHistory')}
                                    role="tab"
                                >
                                    <FaHistory className="me-2" size={14} />
                                    Quotation History
                                </button>
                            </li>
                        </WithQuoPermission> */}

                        <WithExpensePermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'expense' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('expense')}
                                    role="tab"
                                >
                                    Expenses
                                </button>
                            </li>
                        </WithExpensePermission>

                        {!isSubproject && (
                            <WithCommuniPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'communication' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('communication')}
                                        role="tab"
                                    >
                                        Communications
                                    </button>
                                </li>
                            </WithCommuniPermission>
                        )}

                        {!isSales && (
                            <WithContactsPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'contactList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('contactList')}
                                        role="tab"
                                    >
                                        <FaUsers className="me-2" size={14} />
                                        Contacts
                                    </button>
                                </li>
                            </WithContactsPermission>
                        )}

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'projectTeam' ? 'active' : ''}`}
                                onClick={() => handleTabClick('projectTeam')}
                                role="tab"
                            >
                                <FaUsersCog className="me-2" size={14} />
                                Project Team
                            </button>
                        </li>

                        {/* <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'signoffs' ? 'active' : ''}`}
                                onClick={() => handleTabClick('signoffs')}
                                role="tab"
                            >
                                ✅ Sign-offs
                            </button>
                        </li>

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'workflow' ? 'active' : ''}`}
                                onClick={() => handleTabClick('workflow')}
                                role="tab"
                            >
                                🔄 Workflow
                            </button>
                        </li> */}

                      

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'projectDetails' ? 'active' : ''}`}
                                onClick={() => handleTabClick('projectDetails')}
                                role="tab"
                            >
                                Project Details
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Tab Content */}
                <div className="tab-content p-4 bg-white" style={{ minHeight: '500px', overflow: 'visible' }}>
                    {/* Sub Projects Tab */}
                    {activeTab === "subprojectList" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectList
                                clientId={projectDetails?.client_id}
                                parentId={projectId || id}
                                onCloseOffcanvas={temporarilyHideOffcanvas}
                                onReopenOffcanvas={reopenOffcanvas}
                            />
                        </div>
                    )}

                    {/* Quotations Tab */}
                    {activeTab === "quotationList" && projectDetails && (
                        <div className="tab-pane active">
                            <QuotationList
                                projectId={projectId || id}
                                clientId={projectDetails?.client_id || projectDetails?.client?.id}
                                projectName={projectDetails?.name}
                                clientName={projectDetails?.client?.name}
                                clientCurrency={projectDetails?.client?.currency}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                            />
                        </div>
                    )}

                    {/* Quotation History Tab */}
                    {activeTab === "quotationHistory" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="contacts-table-modern">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th scope="col">SL No</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Quotation ID</th>
                                            <th scope="col">Action</th>
                                            <th scope="col">Description</th>
                                            <th scope="col">Document</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotationHistory.map((quotation, index) => (
                                            quotation.history && quotation.history.map((historyItem, idx) => (
                                                <tr key={`${index}-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>{formatDate(quotation.date)}</td>
                                                    <td>{quotation.ref_no}</td>
                                                    <td>{formatAction(historyItem.action)}</td>
                                                    <td>{historyItem.description}</td>
                                                    <td>
                                                        {historyItem.attachment ? (
                                                            <a
                                                                href={`${process.env.REACT_APP_BACKEND_URL}/storage/${historyItem.attachment}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-primary"
                                                            >
                                                                View
                                                            </a>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))
                                        ))}
                                        {quotationHistory.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5 text-muted">
                                                    No quotation history available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Expenses Tab */}
                    {activeTab === "expense" && projectDetails && (
                        <div className="tab-pane active">
                            <ExpenseList
                                projectId={projectId || id}
                                projectDetails={projectDetails}
                            />
                        </div>
                    )}

                    {/* Communications Tab */}
                    {activeTab === "communication" && projectDetails && (
                        <div className="tab-pane active">
                            <CommunicationDetails
                                projectId={projectId || id}
                            />
                        </div>
                    )}

                    {/* Contacts Tab */}
                    {activeTab === "contactList" && projectDetails && (
                        <div className="tab-pane active">
                            <SelectedContact
                                projectId={projectDetails?.id || projectId || id}
                                entityType="project"
                                fetchProjectData={fetchProjectDetails}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                                onCloseOffcanvas={temporarilyHideOffcanvas}
                                onReopenOffcanvas={reopenOffcanvas}
                                showContactModal={forceShowContactModal}
                                setShowContactModal={setForceShowContactModal}
                            />
                        </div>
                    )}

                    {/* Project Team Tab */}
                    {activeTab === "projectTeam" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectTeam 
                                projectId={projectDetails?.id || projectId || id}
                                projectData={projectDetails}
                                fetchProjectDetails={fetchProjectDetails}
                                onAddContact={() => setForceShowContactModal(true)}
                                onCloseParent={onHide} 
                                onMinimizeParent={temporarilyHideOffcanvas}
                                onReopenParent={reopenOffcanvas}
                            />
                        </div>
                    )}

                    {/* Sign-offs Tab */}
                    {activeTab === "signoffs" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectSignoff
                                projectId={projectDetails?.id || projectId || id}
                            />
                        </div>
                    )}

                    {/* Workflow Tab */}
                    {activeTab === "workflow" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectWorkflow
                                projectId={projectDetails?.id || projectId || id}
                                initialStage={projectDetails?.workflow_stage || 'planning'}
                            />
                        </div>
                    )}

                    {/* Project Details Tab */}
                    {activeTab === "projectDetails" && (
                        <div className="tab-pane active">
                            {projectDetails ? (
                                <ProjectView
                                    data={projectDetails}
                                    setProjectDetails={setProjectDetails}
                                />
                            ) : (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading project details...</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Orders Tab */}
                    {activeTab === "orders" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0 text-muted">Total Orders: {projectOrders.length}</h6>
                                <button 
                                    type="button"
                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-plus"
                                    onClick={() => handleShowOrderModal()}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title="Add New PO"
                                >
                                    <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                                </button>
                            </div>
                            <div className="contacts-table-modern" style={{ overflow: 'visible' }}>
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th scope="col">SL No</th>
                                            <th scope="col">PO Number</th>
                                            <th scope="col">Issue Date</th>
                                            <th scope="col">Deal Name</th>
                                            <th scope="col" className="text-end">Order Value</th>
                                            <th scope="col" className="text-end">Recurring Cost</th>
                                            <th scope="col" className="text-end">One-Time Cost</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projectOrders.map((deal, index) => (
                                            <tr key={`${deal.source}-${deal.id}`}>
                                                <td>{index + 1}</td>
                                                <td 
                                                    className="fw-medium text-primary"
                                                    style={{ cursor: deal.po_attachment ? 'pointer' : 'default' }}
                                                    onClick={() => deal.po_attachment && handleFilePreview(`${process.env.REACT_APP_BACKEND_URL}/storage/${deal.po_attachment}`, `PO: ${deal.po_number || deal.external_id}`)}
                                                >
                                                    {deal.po_number || deal.external_id}
                                                    {deal.po_attachment && <i className="mdi mdi-attachment ms-1 text-muted font-size-14" title="View Attachment"></i>}
                                                </td>
                                                <td className="text-muted">{deal.issue_date ? formatDate(deal.issue_date) : '-'}</td>
                                                <td>
                                                    <div className="text-muted" style={{ fontSize: '11px' }}>{deal.name}</div>
                                                </td>
                                                <td className="text-end fw-bold">{parseFloat(deal.po_value || deal.total_value || deal.value || 0).toLocaleString()}</td>
                                                <td className="text-end text-primary">{parseFloat(deal.recurring_cost || 0).toLocaleString()}</td>
                                                <td className="text-end">{parseFloat(deal.one_time_cost || 0).toLocaleString()}</td>
                                                <td className="text-center">
                                                    <div className="dropdown">
                                                        <i 
                                                            className="mdi mdi-dots-vertical font-size-18" 
                                                            data-bs-toggle="dropdown" 
                                                            data-bs-boundary="viewport"
                                                            aria-expanded="false" 
                                                            style={{ cursor: 'pointer' }}
                                                        ></i>
                                                        <ul className="dropdown-menu dropdown-menu-end" style={{ zIndex: 10000 }}>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => handleShowOrderModal(deal)}>
                                                                    <i className="mdi mdi-refresh me-2 text-primary"></i> Renewal
                                                                </button>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <button 
                                                                    className="dropdown-item text-danger" 
                                                                    onClick={() => handleDeleteOrder(deal)}
                                                                >
                                                                    <i className="mdi mdi-delete me-2"></i> Delete
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {projectOrders.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5 text-muted">
                                                    No project orders found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Vendor POs Tab */}
                    {activeTab === "vendorPOs" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="contacts-table-modern">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th scope="col">SL No</th>
                                            <th scope="col">PO Reference</th>
                                            <th scope="col">Issue Date</th>
                                            <th scope="col">Vendor</th>
                                            <th scope="col" className="text-end">Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projectVendorPOs.map((po, index) => (
                                            <tr key={`po-${po.id}`}>
                                                <td>{index + 1}</td>
                                                <td 
                                                    className="fw-medium text-info"
                                                    style={{ cursor: po.document ? 'pointer' : 'default' }}
                                                    onClick={() => po.document && handleFilePreview(`${process.env.REACT_APP_BACKEND_URL}/storage/${po.document}`, `Vendor PO: ${po.ref_no}`)}
                                                >
                                                    {po.ref_no}
                                                    {po.document && <i className="mdi mdi-attachment ms-1 text-muted font-size-14" title="View Attachment"></i>}
                                                </td>
                                                <td className="text-muted">{po.date ? formatDate(po.date) : '-'}</td>
                                                <td>
                                                    <div className="text-dark fw-medium small">{po.vendor?.name}</div>
                                                </td>
                                                <td className="text-end fw-bold">{parseFloat(po.total_amount || po.amount || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        {projectVendorPOs.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5 text-muted">
                                                    No vendor purchase orders found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Offcanvas.Body>

            </Offcanvas>

            {/* File Preview Offcanvas */}
            <Offcanvas 
                show={showPreview} 
                onHide={() => setShowPreview(false)} 
                placement="end" 
                style={{ width: '60%', zIndex: 1060 }}
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">{previewTitle}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0 overflow-hidden">
                    {previewFile ? (
                        <iframe
                            src={previewFile}
                            title="File Preview"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    ) : (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <p className="text-muted">No preview available</p>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {/* Project Modal */}
            {showModal && (
                <ProjectModal
                    showModal={showModal}
                    handleClose={() => setShowModal(false)}
                    projectId={projectId || id}
                    projectData={selectedProject}
                    editProject={editProject}
                    clients={clients}
                    fetchClients={fetchClients}
                    defaultClientId={selectedProject?.client_id}
                />
            )}

            {/* New Order / Renewal Offcanvas */}
            <Offcanvas 
                show={showOrderModal} 
                onHide={() => !isSubmittingOrder && handleCloseOrderModal()} 
                placement="end" 
                style={{ width: '600px', zIndex: 1060 }}
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold text-primary">
                        {orderFormData.isRenewal ? 'Renewal Order' : 'Create New Order'}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form onSubmit={handleOrderSubmit}>
                        <div className="row">
                          
                            <div className="col-12 mb-3">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">PO Number</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={orderFormData.po_number}
                                        onChange={(e) => setOrderFormData({...orderFormData, po_number: e.target.value})}
                                        required
                                        placeholder="Enter PO Number"
                                        className="form-feild"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12 mb-3">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">Issue Date</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={orderFormData.issue_date}
                                        onChange={(e) => setOrderFormData({...orderFormData, issue_date: e.target.value})}
                                        required
                                        className="form-feild"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12 mb-3">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">One-Time Cost (Deployment)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.01"
                                        value={orderFormData.one_time_cost}
                                        onChange={(e) => {
                                            const ot = parseFloat(e.target.value) || 0;
                                            const rc = parseFloat(orderFormData.recurring_cost) || 0;
                                            setOrderFormData({...orderFormData, one_time_cost: e.target.value, po_value: (ot + rc).toFixed(2)});
                                        }}
                                        placeholder="0.00"
                                        className="form-feild"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12 mb-3">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">Recurring Cost</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.01"
                                        value={orderFormData.recurring_cost}
                                        onChange={(e) => {
                                            const rc = parseFloat(e.target.value) || 0;
                                            const ot = parseFloat(orderFormData.one_time_cost) || 0;
                                            setOrderFormData({...orderFormData, recurring_cost: e.target.value, po_value: (ot + rc).toFixed(2)});
                                        }}
                                        placeholder="0.00"
                                        className="form-feild"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12 mb-3">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">Total Order Value</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.01"
                                        value={orderFormData.po_value}
                                        onChange={(e) => setOrderFormData({...orderFormData, po_value: e.target.value})}
                                        required
                                        placeholder="0.00"
                                        className="form-feild fw-bold text-primary bg-light"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-12 mb-4">
                                <Form.Group>
                                    <Form.Label className="fw-medium text-dark">PO Attachment</Form.Label>
                                    <Form.Control 
                                        type="file" 
                                        onChange={(e) => setOrderFormData({...orderFormData, po_attachment: e.target.files[0]})}
                                        className="form-control"
                                    />
                                    <Form.Text className="text-muted small">Upload signed PO (PDF/JPG/PNG).</Form.Text>
                                </Form.Group>
                            </div>
                        </div>
                        <div className="d-flex gap-2 pt-3 border-top bg-white">
                            <Button variant="light" className="flex-grow-1" onClick={handleCloseOrderModal} disabled={isSubmittingOrder}>
                                Cancel
                            </Button>
                            <Button variant="primary" className="flex-grow-1" type="submit" disabled={isSubmittingOrder}>
                                {isSubmittingOrder ? (
                                    <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> Processing...</>
                                ) : (
                                    orderFormData.isRenewal ? 'Confirm Renewal' : 'Create Order'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default ProjectDetails;
